import {
  type V1FormElement,
  type V1FormElementUiDateRangeKubunSelectBox,
  V1FormElementUiType,
  type V1FormElementContentState,
  type V1FormElementState,
  type V1FormElementUiAreaSelect,
  type V1FormElementUiCheckBoxList,
  type V1FormElementUiDateSelectBox,
  type V1FormElementUiOccupationCategorySelect,
  type V1FormElementUiRadioList,
  type V1FormElementUiSelectBox,
  type V1FormElementUiSelectSearch,
  type V1FormElementUiTextField,
  type V1FormElementUiZipCode,
  V1FormElementValidateType,
  V1FormElementSetLayoutDirection
} from 'mnvm-mypage-app-ts-if'

import { Block } from '@/components/base/Block'
import { memo } from 'react'
import { generateUUID } from '../Form/uitl'
import { FormELementTextAndSelectorPopup } from './FormELementTextAndSelectorPopup'
import FormElementAreaSelect from './FormElementAreaSelect'
import FormElementCheckboxList from './FormElementCheckBoxList'
import FormElementCheckBoxListSimple from './FormElementCheckBoxListSimple'
import { FormElementComboBoxAndSelectorPopup } from './FormElementComboBoxAndSelectorPopup'
import FormElementOccupationCategoryRadioList from './FormElementOccupationCategoryRadioList'
import FormElementSearchField from './FormElementSearchField'
import FormElementUserIdField from './FormElementUserIdField'
import FormElementZipCode from './FormElementZipCode'
import { type FormElementStyle } from './FormElements'
import FormElementSelectorPopupButtons from './FormElementsSelectorPopupButtons/FormElementSelectorPopupButtons'
import FormLabel from './FormLabel'
import FormElementRadioList from './Radio/FormElementRadioList'
import { FormElementDateRangeBox } from './SelectBox/FormElementDateRangeBox'
import { FormElementDateSelectBox } from './SelectBox/FormElementDateSelectBox'
import { FormElementSelectBox } from './SelectBox/FormElementSelectBox'
import FormElementTextAreaField from './TextField/FormElementTextAreaField'
import FormElementTextField from './TextField/FormElementTextField'
import { FormElementDateRangeKubun } from './SelectBox/FormElementDateRangeKubun'
import FormElementTextFieldEmail from './TextField/FormElementTextFieldEmail'
import useIsMobile from '@/hooks/useMobile'
import spaceStyle from '@/styles/space'
import { type TJobPostingAdvancedSearchConditionBackup } from '@/hooks/useJobPostingAdvancedSearchConditionBackup'
import FormElementNoInput from './FormElementNoInput'

interface Props {
  isEdit?: boolean
  formElement: V1FormElement
  formElementState: V1FormElementState
  formElementContents?: V1FormElementContentState[]
  formElementStyle?: FormElementStyle
  elemenErrorMessage?: string
  searchAddressByZipCode?: (
    formElementUiZipCode: V1FormElementUiZipCode,
    formElementState: V1FormElementState,
    address: string
  ) => Promise<void>
  handleStationTrainLinesModal?: () => void
  onChange: (
    newFormElementState: V1FormElementState,
    conditionBackup?: TJobPostingAdvancedSearchConditionBackup
  ) => void
  onSelectElement?: (uiType: V1FormElementUiType, element: any) => void
  isHorizontalLayoutProfile?: boolean
  elementIndex?: number
  formElementSetLayout?: string
}

const FormElement = ({
  isEdit = true,
  formElement,
  formElementState,
  formElementContents,
  formElementStyle,
  elemenErrorMessage,
  searchAddressByZipCode,
  handleStationTrainLinesModal,
  onChange,
  onSelectElement,
  isHorizontalLayoutProfile,
  elementIndex,
  formElementSetLayout
}: Props): JSX.Element => {
  const isMobile = useIsMobile()

  const buildChild = (): JSX.Element => {
    switch (formElement?.uiType) {
      case V1FormElementUiType.ZipCode: {
        return (
          <FormElementZipCode
            isEdit={isEdit}
            zipCodeUiIFM={formElement.ui as V1FormElementUiZipCode}
            name={`${formElement.state?.key ?? ''}_${generateUUID()}`}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            searchAddressByZipCode={searchAddressByZipCode}
            onChange={onChange}
          />
        )
      }
      case V1FormElementUiType.CheckBoxList: {
        return (
          <FormElementCheckboxList
            checkBoxListUiIFM={formElement.ui as V1FormElementUiCheckBoxList}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            onChange={onChange}
          />
        )
      }
      case V1FormElementUiType.SimpleCheckBoxList: {
        return (
          <FormElementCheckBoxListSimple
            isEdit={isEdit}
            checkBoxListSimpleUiIFM={
              formElement.ui as V1FormElementUiCheckBoxList
            }
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            onChange={onChange}
            layoutDirection={formElement?.ui?.layoutDirection}
            formElementSetLayout={formElementSetLayout}
          />
        )
      }
      case V1FormElementUiType.RadioList: {
        return (
          <FormElementRadioList
            isEdit={isEdit}
            radioListUiIFM={formElement.ui as V1FormElementUiRadioList}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            onChange={onChange}
          />
        )
      }
      case V1FormElementUiType.SelectBox:
        return (
          <FormElementSelectBox
            isEdit={isEdit}
            selectBoxUiIFM={formElement.ui as V1FormElementUiSelectBox}
            formElementState={formElementState}
            formElementContents={formElementContents}
            elemenErrorMessage={elemenErrorMessage}
            onChange={onChange}
            style={formElementStyle?.selectBox}
            isHorizontalLayoutProfile={isHorizontalLayoutProfile}
          />
        )
      case V1FormElementUiType.TextField:
        if (formElement?.validateType === V1FormElementValidateType.Email) {
          return (
            <FormElementTextFieldEmail
              isEdit={isEdit}
              name={`${formElement.state?.key ?? ''}_${generateUUID()}`}
              textFieldUiIFM={formElement.ui as V1FormElementUiTextField}
              formElementState={formElementState}
              elemenErrorMessage={elemenErrorMessage}
              onChange={onChange}
            />
          )
        } else {
          return (
            <FormElementTextField
              isEdit={isEdit}
              name={`${formElement.state?.key ?? ''}_${generateUUID()}`}
              textFieldUiIFM={formElement.ui as V1FormElementUiTextField}
              formElementState={formElementState}
              elemenErrorMessage={elemenErrorMessage}
              validateType={formElement.validateType}
              onChange={onChange}
              style={formElementStyle?.textField}
              formElementSetLayout={formElementSetLayout}
            />
          )
        }
      case V1FormElementUiType.TextAreaField:
        return (
          <FormElementTextAreaField
            isEdit={isEdit}
            textFieldUiIFM={formElement.ui as V1FormElementUiTextField}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            onChange={onChange}
          />
        )
      case V1FormElementUiType.DateSelectBox:
        return (
          <FormElementDateSelectBox
            isEdit={isEdit}
            uiIFM={formElement.ui as V1FormElementUiDateSelectBox}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            onChange={onChange}
            style={formElementStyle?.selectBox}
          />
        )
      case V1FormElementUiType.SelectSearch:
        return (
          <FormElementSearchField
            isEdit={isEdit}
            name={''}
            selectSearchUiIFM={formElement.ui as V1FormElementUiSelectSearch}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            handleStationTrainLinesModal={handleStationTrainLinesModal}
            onChange={(element) => {
              if (onSelectElement !== undefined) {
                onSelectElement(V1FormElementUiType.AreaSelect, element)
              }
            }}
          />
        )
      case V1FormElementUiType.AreaSelect:
        return (
          <FormElementAreaSelect
            areaSelectUiIFM={formElement.ui as V1FormElementUiAreaSelect}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            onClick={(element) => {
              if (onSelectElement !== undefined) {
                onSelectElement(V1FormElementUiType.AreaSelect, element)
              }
            }}
          />
        )
      case V1FormElementUiType.OccupationCategoryCheckboxList:
        return (
          <FormElementOccupationCategoryRadioList
            occupationCategoryUiIFM={
              formElement.ui as V1FormElementUiOccupationCategorySelect
            }
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            onClick={(element) => {
              if (onSelectElement !== undefined) {
                onSelectElement(
                  V1FormElementUiType.OccupationCategoryCheckboxList,
                  element
                )
              }
            }}
          />
        )
      case V1FormElementUiType.SelectorPopupButtons:
        return (
          <FormElementSelectorPopupButtons
            form={formElement}
            onChange={onChange}
            elemenErrorMessage={elemenErrorMessage}
          />
        )
      case V1FormElementUiType.TextAndSelectorPopup:
        return (
          <FormELementTextAndSelectorPopup
            isEdit={isEdit}
            form={formElement}
            onChange={onChange}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            isHorizontalLayoutProfile={isHorizontalLayoutProfile}
          />
        )
      case V1FormElementUiType.UserId:
        return (
          <FormElementUserIdField
            isEdit={isEdit}
            formElementState={formElementState}
          />
        )
      case V1FormElementUiType.ComboBoxAndSelectorPopup:
        return (
          <FormElementComboBoxAndSelectorPopup
            isEdit={isEdit}
            form={formElement}
            onChange={onChange}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
          />
        )
      case V1FormElementUiType.DateRangeSelectBox:
        return (
          <FormElementDateRangeBox
            isEdit={isEdit}
            uiIFM={formElement.ui as V1FormElementUiDateSelectBox}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            onChange={onChange}
            style={formElementStyle?.selectBox}
          />
        )
      case V1FormElementUiType.DateRangeKubun:
        return (
          <FormElementDateRangeKubun
            isEdit={isEdit}
            uiIFM={formElement.ui as V1FormElementUiDateRangeKubunSelectBox}
            formElementState={formElementState}
            elemenErrorMessage={elemenErrorMessage}
            onChange={onChange}
            style={formElementStyle?.selectBox}
          />
        )
      default:
        console.log(`${formElement?.uiType?.toString() ?? ''} not implemented`)
        return <></>
    }
  }

  const widthCpnProfileExperience = (): string | undefined => {
    if ((isHorizontalLayoutProfile ?? false) && isMobile) {
      switch (formElement?.uiType) {
        case V1FormElementUiType.SelectBox:
          return 'calc(50% - 6px)' // 12px gap
        case V1FormElementUiType.TextAndSelectorPopup:
          return '100%'

        default:
          break
      }
    } else if (isStyleThreeColumns) {
      return isMobile ? 'calc(50% - 6px)' : 'calc(33.33% - 16px)'
    } else return 'auto'
  }

  const isStyleThreeColumns =
    formElementSetLayout === V1FormElementSetLayoutDirection.ThreeColumns

  const isStyleHorizontalWithRange =
    isEdit &&
    formElementSetLayout === V1FormElementSetLayoutDirection.HorizontalWithRange

  const isStyleHorizontalEvenly =
    formElementSetLayout === V1FormElementSetLayoutDirection.HorizontalEvenly

  const isShowLabelOnView =
    (formElement?.isLabelVisibleOnView ?? false) && !isEdit
  const isShowLabelOnEdit =
    (formElement?.isLabelVisibleOnEdit ?? false) && isEdit

  if (formElement.uiType === V1FormElementUiType.NoInput)
    return (
      <FormElementNoInput
        formElement={formElement}
        isShowLabelOnView={isShowLabelOnView}
        isShowLabelOnEdit={isShowLabelOnEdit}
      />
    )

  return (
    <Block
      width={widthCpnProfileExperience()}
      margin={{
        left:
          elementIndex === 3 && !isMobile && !isEdit
            ? spaceStyle.XXXXXL
            : spaceStyle.NONE
      }}
      style={{
        flex:
          isStyleHorizontalWithRange || isStyleHorizontalEvenly ? 1 : undefined
      }}
    >
      {formElement?.label !== undefined &&
        (isShowLabelOnView || isShowLabelOnEdit) && (
          <FormLabel>{formElement.label}</FormLabel>
        )}
      {buildChild()}
    </Block>
  )
}

export default memo(FormElement)
